/**
 * Proration utilities for upsell/downsell billing changes. Moved from
 * src/utils/prorationUtils.js - upsell/downsell is this logic's only real
 * consumer (grepped project-wide before moving), so it moves in rather than
 * staying an external dependency. Math is unchanged from the original;
 * debug console.log calls are dropped per the migration skill's "remove
 * debug noise" rule (rule 9) - none of them were behind a dev-only guard,
 * and none log anything actionable a `console.warn` would be more
 * appropriate for.
 */

/**
 * Calculate proration ratio based on time remaining in billing period.
 * @param {Date} periodStart - Start date of current billing period
 * @param {Date} periodEnd - End date of current billing period
 * @param {Date} changeDate - Date when the change/upgrade happens
 * @returns {number} Proration ratio (0 to 1)
 */
export function calculateProrationRatio(periodStart, periodEnd, changeDate) {
  const totalSeconds = (periodEnd - periodStart) / 1000;
  if (totalSeconds <= 0) return 0;
  const secondsRemaining = (periodEnd - changeDate) / 1000;
  return Math.max(0, secondsRemaining / totalSeconds);
}

/**
 * Calculate prorated charge for a single item.
* @param {Object} params
 * @param {Date} params.periodStart
 * @param {Date} params.periodEnd
 * @param {Date} params.changeDate
 * @param {number} params.oldAmount
 * @param {number} params.newAmount
 * @returns {Object} Proration details
 */
export function calculateProration({ periodStart, periodEnd, changeDate, oldAmount, newAmount }) {
  const prorationRatio = calculateProrationRatio(periodStart, periodEnd, changeDate);
  const changeAmount = newAmount - oldAmount;
  const proratedCharge = changeAmount * prorationRatio;

  return {
    prorationRatio,
    changeAmount,
    proratedCharge: Math.round(proratedCharge * 100) / 100,
  };
}

/**
 * D14 - a subscription's plan/product subdocument can carry `_id` populated
 * to the full catalog entity (e.g. the `getSubscriptionsFromCompanyId` API
 * the upsell wizard's "select subscription" step calls populates
 * `plans._id`/`products._id` to the whole Plan/Product document), while the
 * wizard's own "new" side (`toBillingSubmitPayload`'s `_id: item.entityId`)
 * always carries a bare id string. Comparing `a._id === b._id` directly
 * then compares an object to a string and is never true - matching this
 * codebase's own established `item?._id?._id ?? item?._id` unwrap (see
 * `resolveItemName`/`resolveMissingMarket` in useUpsellWizard.js,
 * `serializePlanForSubscription` in executeUpsellOnSubscription.js) rather
 * than inventing a new pattern here.
 */
export function resolveEntityId(item) {
  const raw = item?._id?._id ?? item?._id ?? item?.id;
  return raw === undefined || raw === null ? null : String(raw);
}

/**
 * Calculate prorated charges for multiple billing items (plans, products, line items).
 */
export function calculateProrationForItems({
  periodStart,
  periodEnd,
  changeDate,
  oldItems = [],
  newItems = [],
  itemType,
}) {
  const prorationRatio = calculateProrationRatio(periodStart, periodEnd, changeDate);
  let totalChangeAmount = 0;
  let totalProratedCharge = 0;
  const itemDetails = [];

  // Name compared case/whitespace-insensitively - same defensive reasoning
  // as the `_id` unwrap above, since a catalog-name backfill and a
  // wizard-entered label are two independently-sourced strings that
  // shouldn't fail to match over incidental casing/trimming differences.
  const sameName = (x, y) =>
    !!x && !!y && String(x).trim().toLowerCase() === String(y).trim().toLowerCase();

  const findMatch = (a, b) => {
    const idA = resolveEntityId(a);
    const idB = resolveEntityId(b);
    if (idA && idB && idA === idB) return true;
    if (itemType === "plans") return sameName(a.planName, b.planName);
    if (itemType === "products") return sameName(a.productName, b.productName);
    if (itemType === "lineItems") return sameName(a.name, b.name);
    return false;
  };

  newItems.forEach((newItem) => {
    const oldItem = oldItems.find((old) => findMatch(old, newItem));
    // `accordionData?.totalPrice` covers plans/products; line items (one-time
    // fees) carry their computed total at the top level instead - see
    // toLineItem() in _components/billing/toSubmitShape.js, which always
    // includes `totalPrice`, never `accordionData`. Without this fallback
    // every line item's amount silently resolved to 0.
    const oldAmount = oldItem
      ? parseFloat(oldItem.accordionData?.totalPrice) ||
        parseFloat(oldItem.totalPrice) ||
        parseFloat(oldItem.amount) ||
        parseFloat(oldItem.price) ||
        0
      : 0;
    const newAmount =
      parseFloat(newItem.accordionData?.totalPrice) ||
      parseFloat(newItem.totalPrice) ||
      parseFloat(newItem.amount) ||
      parseFloat(newItem.price) ||
      0;

    const changeAmount = newAmount - oldAmount;
    const proratedCharge = changeAmount * prorationRatio;

    itemDetails.push({
      itemId: newItem._id,
      itemName: newItem.planName || newItem.productName || newItem.name,
      oldAmount,
      newAmount,
      changeAmount,
      proratedCharge: Math.round(proratedCharge * 100) / 100,
    });

    totalChangeAmount += changeAmount;
    totalProratedCharge += proratedCharge;
  });

  oldItems.forEach((oldItem) => {
    const stillExists = newItems.some((newItem) => findMatch(oldItem, newItem));
    if (!stillExists) {
      const oldAmount =
        parseFloat(oldItem.accordionData?.totalPrice) ||
        parseFloat(oldItem.totalPrice) ||
        parseFloat(oldItem.amount) ||
        parseFloat(oldItem.price) ||
        0;
      const changeAmount = -oldAmount;
      const proratedCharge = changeAmount * prorationRatio;

      itemDetails.push({
        itemId: oldItem._id,
        itemName: oldItem.planName || oldItem.productName || oldItem.name,
        oldAmount,
        newAmount: 0,
        changeAmount,
        proratedCharge: Math.round(proratedCharge * 100) / 100,
        removed: true,
      });

      totalChangeAmount += changeAmount;
      totalProratedCharge += proratedCharge;
    }
  });

  return {
    prorationRatio,
    totalChangeAmount: Math.round(totalChangeAmount * 100) / 100,
    totalProratedCharge: Math.round(totalProratedCharge * 100) / 100,
    itemDetails,
  };
}

/**
 * Calculate comprehensive proration for an entire subscription change.
 */
export function calculateCompleteProration({
  periodStart,
  periodEnd,
  changeDate,
  oldSubscription,
  newBillingItems,
}) {
  const oldBillingItems = {
    plans: oldSubscription.plans || [],
    products: oldSubscription.products || [],
    lineItems: oldSubscription.lineItems || [],
    discount: oldSubscription.discount || { type: "Flat", value: "0" },
    taxes: oldSubscription.taxes || [],
  };

  const plansProration = calculateProrationForItems({
    periodStart,
    periodEnd,
    changeDate,
    oldItems: oldBillingItems.plans,
    newItems: newBillingItems.plans || [],
    itemType: "plans",
  });

  const productsProration = calculateProrationForItems({
    periodStart,
    periodEnd,
    changeDate,
    oldItems: oldBillingItems.products,
    newItems: newBillingItems.products || [],
    itemType: "products",
  });

  const lineItemsProration = calculateProrationForItems({
    periodStart,
    periodEnd,
    changeDate,
    oldItems: oldBillingItems.lineItems,
    newItems: newBillingItems.lineItems || [],
    itemType: "lineItems",
  });

  const oldDiscount = oldBillingItems.discount;
  const newDiscount = newBillingItems.discount || { type: "Flat", value: "0" };

  let discountProration = { totalProratedCharge: 0, itemDetails: [] };
  if (oldDiscount.type !== newDiscount.type || oldDiscount.value !== newDiscount.value) {
    const oldDiscountAmount = parseFloat(oldDiscount.value) || 0;
    const newDiscountAmount = parseFloat(newDiscount.value) || 0;
    const changeAmount = newDiscountAmount - oldDiscountAmount;
    const prorationRatio = calculateProrationRatio(periodStart, periodEnd, changeDate);
    const proratedCharge = changeAmount * prorationRatio;

    discountProration = {
      totalProratedCharge: Math.round(proratedCharge * 100) / 100,
      itemDetails: [
        {
          itemName: "Discount",
          oldAmount: oldDiscountAmount,
          newAmount: newDiscountAmount,
          changeAmount,
          proratedCharge: Math.round(proratedCharge * 100) / 100,
        },
      ],
    };
  }

  const totalProratedAmount =
    plansProration.totalProratedCharge +
    productsProration.totalProratedCharge +
    lineItemsProration.totalProratedCharge +
    discountProration.totalProratedCharge;

  return {
    prorationRatio: plansProration.prorationRatio,
    totalProratedAmount: Math.round(totalProratedAmount * 100) / 100,
    breakdown: {
      plans: plansProration,
      products: productsProration,
      lineItems: lineItemsProration,
      discount: discountProration,
    },
    summary: {
      periodStart: periodStart.toISOString(),
      periodEnd: periodEnd.toISOString(),
      changeDate: changeDate.toISOString(),
      prorationRatio: plansProration.prorationRatio,
      totalProratedAmount: Math.round(totalProratedAmount * 100) / 100,
    },
  };
}

function daysInMonthUTC(year, month0) {
  return new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
}

function clampDay(year, month0, day) {
  return Math.min(day, daysInMonthUTC(year, month0));
}

function addMonthsClampedUTC(d, monthsToAdd) {
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const targetMonth0 = m + monthsToAdd;
  const targetYear = y + Math.floor(targetMonth0 / 12);
  const targetMonth = ((targetMonth0 % 12) + 12) % 12;
  const targetDay = clampDay(targetYear, targetMonth, d.getUTCDate());
  return new Date(
    Date.UTC(
      targetYear,
      targetMonth,
      targetDay,
      d.getUTCHours(),
      d.getUTCMinutes(),
      d.getUTCSeconds(),
      d.getUTCMilliseconds()
    )
  );
}

function addDaysUTC(d, days) {
  return new Date(d.getTime() + days * 24 * 60 * 60 * 1000);
}

const CANONICAL_BILLING_CYCLES = ["Daily", "Weekly", "Monthly", "Quarterly", "Half-yearly", "Yearly"];

// Some saved subscriptions carry a lowercase billingCycle (e.g. "monthly")
// - confirmed live, 8 real records. The switch below matches exact case, so
// without this those records threw "Unsupported billingCycle" (caught by
// the review step, which then silently drops the whole Proration summary).
// This is a tolerant read, not a fix for whatever writes the bad casing -
// see docs/known-issues.md.
function normalizeBillingCycleCasing(cycle) {
  if (!cycle) return cycle;
  const match = CANONICAL_BILLING_CYCLES.find((c) => c.toLowerCase() === String(cycle).toLowerCase());
  return match || cycle;
}

function toUTC(date) {
  return new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds()
    )
  );
}

// One cycle back from a UTC period end, mirroring how one cycle forward is
// computed below - used only for the nextBillingDate-anchored path.
function retreatOneCycleUTC(periodEndUTC, billingCycle) {
  if (billingCycle === "Daily") return addDaysUTC(periodEndUTC, -1);
  if (billingCycle === "Weekly") return addDaysUTC(periodEndUTC, -7);
  const monthsPerCycle = { Monthly: 1, Quarterly: 3, "Half-yearly": 6, Yearly: 12 }[billingCycle];
  return addMonthsClampedUTC(periodEndUTC, -(monthsPerCycle || 1));
}

/**
 * Derive the current billing period (start/end) and "now" as the change
 * date, from a subscription's `createdAt` anchor and `billingCycle`.
 * @param {Object} subscription
 * @param {Date} [now] - defaults to the current time; overridable for tests.
 */
export function getBillingPeriodDates(subscription, now = new Date()) {
  if (!subscription?.createdAt) {
    throw new Error("subscription.createdAt is required");
  }

  const billingCycle = normalizeBillingCycleCasing(subscription.billingCycle) || "Monthly";

  const nowUTC = toUTC(now);

  // Prefer the subscription's own `nextBillingDate` as the period end - the
  // same field the backend's authoritative resolveBillingPeriod
  // (simplifyserver/utils/proration/index.js) treats as ground truth when it
  // computes the amount actually invoiced. The createdAt-anchored fallback
  // below recomputes its own guess at the period boundaries instead of
  // reading it, and the two can drift apart (e.g. addBillingCycleUtc on the
  // backend clamps to UTC midnight before advancing, cron renewals move
  // nextBillingDate independently of createdAt) - confirmed live on a real
  // subscription, where it silently made this preview disagree with the
  // amount the backend went on to invoice. Falls through to the createdAt
  // computation when nextBillingDate is absent/invalid, or would produce a
  // non-positive period.
  const rawNextBillingDate = subscription.nextBillingDate && new Date(subscription.nextBillingDate);
  if (rawNextBillingDate && !Number.isNaN(rawNextBillingDate.getTime())) {
    const periodEndUTC = toUTC(rawNextBillingDate);
    const periodStartUTC = retreatOneCycleUTC(periodEndUTC, billingCycle);
    if (periodEndUTC.getTime() > periodStartUTC.getTime()) {
      return {
        periodStart: new Date(periodStartUTC.getTime()),
        periodEnd: new Date(periodEndUTC.getTime()),
        changeDate: nowUTC,
      };
    }
  }

  const anchorUTC = toUTC(new Date(subscription.createdAt));

  let periodStartUTC;
  let periodEndUTC;

  switch (billingCycle) {
    case "Daily": {
      const msPerDay = 24 * 60 * 60 * 1000;
      const diffDays = Math.floor((nowUTC.getTime() - anchorUTC.getTime()) / msPerDay);
      const t = Math.max(0, diffDays);
      periodStartUTC = addDaysUTC(anchorUTC, t);
      periodEndUTC = addDaysUTC(periodStartUTC, 1);
      break;
    }
    case "Weekly": {
      const msPerWeek = 7 * 24 * 60 * 60 * 1000;
      const diffWeeks = Math.floor((nowUTC.getTime() - anchorUTC.getTime()) / msPerWeek);
      const t = Math.max(0, diffWeeks);
      periodStartUTC = addDaysUTC(anchorUTC, t * 7);
      periodEndUTC = addDaysUTC(periodStartUTC, 7);
      break;
    }
    case "Monthly":
    case "Quarterly":
    case "Half-yearly":
    case "Yearly": {
      const monthsPerCycle = { Monthly: 1, Quarterly: 3, "Half-yearly": 6, Yearly: 12 }[
        billingCycle
      ];

      // Each boundary is computed as anchor + k*cycles, always relative to
      // the original anchor - not the previous boundary. Stepping forward
      // from the previous boundary compounds any day-of-month clamping (e.g.
      // a day-31 anchor crossing February, clamped to the 28th) into a
      // permanent drift: every later boundary was being computed from the
      // already-clamped date, so the billing anchor silently and permanently
      // shifted from the 31st to the 28th, even in later 31-day months.
      let k = 0;
      let currentPeriodStart = anchorUTC;
      let currentPeriodEnd = addMonthsClampedUTC(anchorUTC, monthsPerCycle);

      while (currentPeriodEnd.getTime() <= nowUTC.getTime()) {
        k += 1;
        currentPeriodStart = addMonthsClampedUTC(anchorUTC, k * monthsPerCycle);
        currentPeriodEnd = addMonthsClampedUTC(anchorUTC, (k + 1) * monthsPerCycle);
      }

      periodStartUTC = currentPeriodStart;
      periodEndUTC = currentPeriodEnd;
      break;
    }
    default:
      throw new Error(`Unsupported billingCycle: ${billingCycle}`);
  }

  return {
    periodStart: new Date(periodStartUTC.getTime()),
    periodEnd: new Date(periodEndUTC.getTime()),
    changeDate: nowUTC,
  };
}
